import { useCallback, useEffect, useRef, useState } from 'react'
import { FetchEventTarget, SseMessageTypeClose, SseMessageTypeData, SseMessageTypeError } from '@/common/ssEventStream'
import { ProposalSseResponseSchema } from '@/api/proposals/proposals'
import type { ProposalSectionMessage, ProposalSseResponse } from '@/api/proposals/proposals'
import { mapProposalErrorMessage } from './errorMessages'
import {
  applyProposalSseEvent,
  clearWarningsForSection,
  initialProposalStreamState,
  makeOptimisticUserTurn,
  makeStreamedAssistantTurn,
  resetForStreamClose
} from './proposalStream'
import type { useRouter } from '@tanstack/react-router'
import type { ProposalStreamState } from './proposalStream'

export interface UseProposalStreamResult extends ProposalStreamState {
  isStreaming: boolean
  optimisticBySectionId: Record<number, ProposalSectionMessage>
  committedBySectionId: Record<number, ProposalSectionMessage>
  start: (
    endpoint: string,
    body: Record<string, unknown>,
    optimistic?: { sectionId: number; userText: string },
    // The section this call is about to rebuild (refine or regenerate). Defaults to the optimistic
    // section, so refine callers do not need to pass it twice.
    targetSectionId?: number
  ) => void
  stop: () => void
  clearError: () => void
  clearSectionBuffers: (sectionId: number) => void
}

export function useProposalStream({
  router,
  onClose
}: {
  router: ReturnType<typeof useRouter>
  onClose?: (closedSectionId: number | null) => void
}): UseProposalStreamResult {
  const [state, setState] = useState<ProposalStreamState>(initialProposalStreamState)
  const [isStreaming, setIsStreaming] = useState(false)
  const [optimisticBySectionId, setOptimisticBySectionId] = useState<Record<number, ProposalSectionMessage>>({})
  const [committedBySectionId, setCommittedBySectionId] = useState<Record<number, ProposalSectionMessage>>({})

  const abortRef = useRef<AbortController | null>(null)
  const deferredAbortRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const streamGenerationRef = useRef(0)

  // The section a refine started optimistically. A refine SSE emits only content frames (no status),
  // so an empty completion never sets activeSectionId; without this fallback the close handler would
  // pass a null section id and the route would never clear that section's optimistic buffer.
  const optimisticSectionIdRef = useRef<number | null>(null)

  // Coalesce content chunks to ~1 render per animation frame. SSE can deliver many small content
  // frames; applying each one synchronously re-renders (and re-parses streamed markdown) per token.
  const rafRef = useRef<number | null>(null)
  const pendingRef = useRef<Array<ProposalSseResponse>>([])

  // stateRef mirrors the SSE-derived state synchronously. The close handler needs the final
  // accumulated state to commit the draft and clear the buffer, and it must do so via plain side
  // effects (not inside a setState updater, which React may invoke more than once). Reading the ref
  // lets those effects run exactly once in the event handler. commitState keeps ref and state in sync.
  const stateRef = useRef<ProposalStreamState>(initialProposalStreamState)
  const commitState = useCallback((next: ProposalStreamState) => {
    stateRef.current = next
    setState(next)
  }, [])

  useEffect(() => {
    if (deferredAbortRef.current) {
      clearTimeout(deferredAbortRef.current)
      deferredAbortRef.current = null
    }
    return () => {
      deferredAbortRef.current = setTimeout(() => {
        streamGenerationRef.current += 1
        abortRef.current?.abort()
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      }, 0)
    }
  }, [])

  const flushPending = useCallback(() => {
    rafRef.current = null
    const batch = pendingRef.current
    if (batch.length === 0) return
    pendingRef.current = []
    commitState(batch.reduce((acc, event) => applyProposalSseEvent(acc, event), stateRef.current))
  }, [commitState])

  const clearSectionBuffers = useCallback((sectionId: number) => {
    setOptimisticBySectionId((m) => {
      if (!(sectionId in m)) return m
      const next = { ...m }
      delete next[sectionId]
      return next
    })
    setCommittedBySectionId((m) => {
      if (!(sectionId in m)) return m
      const next = { ...m }
      delete next[sectionId]
      return next
    })
  }, [])

  const start = useCallback(
    (
      endpoint: string,
      body: Record<string, unknown>,
      optimistic?: { sectionId: number; userText: string },
      targetSectionId?: number
    ) => {
      const generation = ++streamGenerationRef.current
      const isCurrent = () => streamGenerationRef.current === generation
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      pendingRef.current = []
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      // Refine and regenerate target exactly one section, so only that section's prior warnings are
      // cleared; a sibling section's warning survives untouched.
      const clearedSectionId = targetSectionId ?? optimistic?.sectionId
      const preservedWarnings =
        clearedSectionId !== undefined
          ? clearWarningsForSection(stateRef.current.warningsBySectionId, clearedSectionId)
          : {}
      commitState({ ...initialProposalStreamState, warningsBySectionId: preservedWarnings })
      setIsStreaming(true)
      optimisticSectionIdRef.current = optimistic?.sectionId ?? null

      if (optimistic) {
        // Show the user's instruction immediately, and clear any stale committed turn for this
        // section so the previous reply does not linger beside the new request.
        const userTurn = makeOptimisticUserTurn(optimistic.userText, Date.now())
        setOptimisticBySectionId((m) => ({ ...m, [optimistic.sectionId]: userTurn }))
        setCommittedBySectionId((m) => {
          if (!(optimistic.sectionId in m)) return m
          const next = { ...m }
          delete next[optimistic.sectionId]
          return next
        })
      }

      const eventTarget = FetchEventTarget(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
        body: JSON.stringify(body)
      })

      eventTarget.addEventListener(SseMessageTypeData, ((e: MessageEvent<string>) => {
        if (!isCurrent()) return
        try {
          const parsed = ProposalSseResponseSchema.parse(JSON.parse(e.data))
          pendingRef.current.push(parsed)
          if (rafRef.current === null) rafRef.current = requestAnimationFrame(flushPending)
        } catch {
          // ignore malformed chunks
        }
      }) as EventListener)

      eventTarget.addEventListener(SseMessageTypeClose, () => {
        if (!isCurrent()) return
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
        // Flush any buffered chunks synchronously against the ref, then run side effects once. We
        // commit the streamed draft as a turn BEFORE clearing the streaming buffer so there is no
        // empty render window (the end-of-stream flash); both state updates land in the same batch.
        const batch = pendingRef.current
        pendingRef.current = []
        const settled = batch.reduce((acc, event) => applyProposalSseEvent(acc, event), stateRef.current)
        const closedSectionId = settled.activeSectionId ?? optimisticSectionIdRef.current
        const draft = closedSectionId !== null ? settled.streamingBySectionId[closedSectionId] : undefined
        if (closedSectionId !== null && draft && draft.length > 0) {
          const assistantTurn = makeStreamedAssistantTurn(draft, Date.now())
          setCommittedBySectionId((m) => ({ ...m, [closedSectionId]: assistantTurn }))
        }
        setIsStreaming(false)
        commitState(resetForStreamClose(settled))
        router.invalidate({ sync: true }).then(() => {
          if (!isCurrent()) return
          onClose?.(closedSectionId)
        })
      })

      // This is the TRANSPORT-level error (a rejected fetch, or an HTTP response that never made
      // it to SSE, e.g. RefineSection/RegenerateSection failing validation before setSSEHeaders).
      // The IN-BAND SSE error frame (ProposalSseType.Error, handled by applyProposalSseEvent in
      // proposalStream.ts) is the other error path, and it already calls mapProposalErrorMessage.
      // The two cannot both fire for one failure: a request either fails before SSE headers are
      // sent (this listener, exactly once) or after (only the in-band frame, since a successful
      // 200 response never reaches this branch of FetchEventTarget again for that request). So
      // applying the mapper here cannot double-map an already-mapped in-band message.
      eventTarget.addEventListener(SseMessageTypeError, ((e: CustomEvent) => {
        if (!isCurrent()) return
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
        pendingRef.current = []
        setIsStreaming(false)
        const message =
          e.detail instanceof Error ? mapProposalErrorMessage(e.detail.message).message : 'An error occurred'
        commitState({ ...stateRef.current, error: message })
      }) as EventListener)
    },
    [router, onClose, flushPending, commitState]
  )

  const clearError = useCallback(() => commitState({ ...stateRef.current, error: null }), [commitState])

  // stop cancels the in-flight stream. Aborting the fetch cancels the request context, so the
  // backend refine/regenerate returns before persisting the partial draft (see RefineSection). We
  // therefore discard the local optimistic/partial state and let the backend stay the source of
  // truth: invalidate the route, then run onClose so the section refetches and its buffers clear.
  // Aborting dispatches no CLOSE/ERROR (FetchEventTarget swallows AbortError), so this manual
  // reset is the only state transition.
  const stop = useCallback(() => {
    const controller = abortRef.current
    if (!controller || !isStreaming) return
    const stoppedGeneration = ++streamGenerationRef.current
    controller.abort()
    abortRef.current = null
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    pendingRef.current = []
    const stoppedSectionId = stateRef.current.activeSectionId ?? optimisticSectionIdRef.current
    setIsStreaming(false)
    commitState(resetForStreamClose(stateRef.current))
    router.invalidate({ sync: true }).then(() => {
      if (streamGenerationRef.current !== stoppedGeneration) return
      onClose?.(stoppedSectionId)
    })
  }, [isStreaming, commitState, router, onClose])

  return {
    ...state,
    isStreaming,
    optimisticBySectionId,
    committedBySectionId,
    start,
    stop,
    clearError,
    clearSectionBuffers
  }
}
